'use client'
import Image from 'next/image'
import Logo from '~/Image/logo.png'
import Link from 'next/link'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faGooglePlay,
  faAppStoreIos,
  faFacebook,
  faYoutube,
  faTiktok,
  faXTwitter,
} from '@fortawesome/free-brands-svg-icons'
import { ChildCategory } from '@/models/menu.model'
import { HeaderProps } from '../Header/Header'

const Footer = ({ menus = [] }: HeaderProps) => {
  const allItems = menus.flatMap((item) => [
    { id: item.id, name: item.name, slug: `/danh-muc/${item.slug}` },
    ...(item.childCategories || []).map((child: ChildCategory) => ({
      id: child.id,
      name: child.name,
      slug: `/danh-muc/${child.slug}`,
    })),
  ])

  const firstColumnExtraLinks = allItems.slice(0, 3)
  const remainingItems = allItems.slice(3)

  const columns = []
  for (let i = 0; i < remainingItems.length; i += 6) {
    columns.push(remainingItems.slice(i, i + 6))
  }

  return (
    <footer className="bg-bgDark text-sm text-white py-8 border-t">
      <div className="container mx-auto px-4">
        {/* Top Section */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
          {/* About */}
          <div className="col-span-12 md:col-span-4">
            <h2 className="text-lg font-bold mb-4 text-center md:text-left">Về Chúng Tôi</h2>
            <Link href="/">
              <Image
                src={Logo}
                alt="About Logo"
                width={300}
                height={200}
                className="mb-4 block mx-auto md:mx-0"
              />
            </Link>
            <p className="text-gray-400 text-sm text-center md:text-left">
              Trang báo điện tử dành cho cộng đồng người Việt toàn thế giới. Cập nhật tin tức toàn
              cầu 24/7, cung cấp thông tin mới nhất về chính trị, kinh tế, văn hóa và thể thao.
            </p>

            {/* Social Icons */}
            <div className="mt-4 flex justify-center md:justify-start space-x-4">
              <Link
                href="https://www.facebook.com/IamTAPNews"
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="w-10 h-10 bg-[#1877F2] rounded-md flex items-center justify-center hover:opacity-80 border border-gray-800">
                  <FontAwesomeIcon icon={faFacebook} className="text-white text-lg" />
                </div>
              </Link>
              <Link
                href="https://www.youtube.com/@TAPNewsUS/videos"
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="w-10 h-10 bg-[#FF0000] rounded-md flex items-center justify-center hover:opacity-80 border border-gray-800">
                  <FontAwesomeIcon icon={faYoutube} className="text-white text-lg" />
                </div>
              </Link>
              <Link
                href="https://www.tiktok.com/@tapnewsus"
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="w-10 h-10 bg-black rounded-md flex items-center justify-center hover:opacity-80 border border-gray-800">
                  <FontAwesomeIcon icon={faTiktok} className="text-white text-lg" />
                </div>
              </Link>
              <Link href="https://x.com/TAPNewsUS" target="_blank" rel="noopener noreferrer">
                <div className="w-10 h-10 bg-black rounded-md flex items-center justify-center hover:opacity-80 border border-gray-800">
                  <FontAwesomeIcon icon={faXTwitter} className="text-white text-lg" />
                </div>
              </Link>
            </div>
          </div>

          {/* Links + App */}
          <div className="col-span-12 md:col-span-8 flex flex-col md:flex-row gap-8 md:gap-4 items-center md:items-start">
            {/* Menu Columns */}
            <div className="md:flex-1 md:grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 hidden">
              <div>
                <ul className="space-y-2">
                  <li>
                    <Link href="/">
                      <span className="hover:text-primary">Trang chủ</span>
                    </Link>
                  </li>
                  <li>
                    <Link href="#">
                      <span className="hover:text-primary">Mới nhất</span>
                    </Link>
                  </li>
                  <li>
                    <Link href="#">
                      <span className="hover:text-primary">Xem nhiều</span>
                    </Link>
                  </li>
                  {firstColumnExtraLinks.map((item) => (
                    <li key={item.id}>
                      <Link href={item.slug}>
                        <span className="hover:text-primary">{item.name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              {columns.slice(0, 2).map((column, index) => (
                <div key={index}>
                  <ul className="space-y-2">
                    {column.map((item) => (
                      <li key={item.id}>
                        <Link href={item.slug}>
                          <span className="hover:text-primary">{item.name}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* App Download */}
            <div className="flex flex-col space-y-4">
              <p className="text-white font-bold">Tải ứng dụng :</p>
              <Link
                href="https://play.google.com/store/apps/details?id=com.iamtapnews.app_news&hl=vi"
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="flex items-center bg-gray-800 px-4 py-2 rounded-md hover:bg-gray-700 transition">
                  <FontAwesomeIcon icon={faGooglePlay} className="text-green-400 text-xl mr-4" />
                  <div>
                    <p className="text-xs text-gray-300">GET IT ON</p>
                    <p className="text-sm font-bold text-white">Google Play</p>
                  </div>
                </div>
              </Link>
              <Link
                href="https://apps.apple.com/vn/app/tap-news/id6499132971"
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="flex items-center bg-gray-800 px-4 py-2 rounded-md hover:bg-gray-700 transition">
                  <FontAwesomeIcon icon={faAppStoreIos} className="text-blue-400 text-xl mr-4" />
                  <div>
                    <p className="text-xs text-gray-300">Download on the</p>
                    <p className="text-sm font-bold text-white">App Store</p>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-8 flex flex-col md:flex-row justify-between items-start gap-8 text-center md:text-left">
          <div className="w-full md:w-1/3">
            <p className="text-white">
              Báo điện tử <strong>TAPNews</strong> - Tiếng nói người Việt toàn cầu
            </p>
          </div>
          <div className="w-full md:w-1/3">
            <p className="text-white font-bold">Địa chỉ:</p>
            <p className="text-white">US Office: 7280 Spring Flower Ave, Las Vegas, NV 89117</p>
            <p className="text-white">VN Office: 5B Phổ Quang, phường Tân Sơn Hoà, TP. HCM, <br/> Việt Nam</p>
            <p className="text-white mt-4">&copy; 1997-2024. Toàn bộ bản quyền thuộc MegaWorld - TAPMediaInc</p>
          </div>
          <div className="w-full md:w-1/3">
            <p className="text-white font-bold">Liên hệ:</p>
            <p className="text-white">Điện thoại: +13605050505 - +84935050505</p>
            <p className="text-white">Email: contact.tapnews@gmail.com</p>
            <Link href="https://tapmediainc.com/" target="_blank" className="text-white">
              Website: tapmediainc.com
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
